/**
Author - Brijesh Pandey
Git - https://bitbucket.org/__brijesh/
**/

import React, {useEffect} from "react";
import Head from "next/head";

import styled from "styled-components";

import SafeLink from "components/commons/SafeLink";

import {
  WealthyEnv,
  triggerToast
} from "helpers";
import {handleApiError} from "helpers";

const copyIcon = `${WealthyEnv.CDN_URL}/wealth-managers/agent-onboarding-copy.svg`;

const whatsappIcon = {
  altImage: "whatsapp-icon",
  image: `${WealthyEnv.HOMEPAGE_CDN_URL}/partners-social-media-icons-whatsapp.svg`,
  url: `${WealthyEnv.WHATSAPP_SHARE_LINK}?text=Register%20as%20a%20Wealthy%20Partner%20and%20start%20your%20wealth%20management%20career.%20Sell%20financial%20products%20anytime%20and%20anywhere%20and%20earn%20regular%20income.%0A%0AJoin%20The%20Wealth%20Platform%20for%20Wealth%20Partners%3A%20http://invite.wlthy.in/?utm_source=share_whatsapp`
};
const twitterIcon = {
  altImage: "twitter-icon",
  image: `${WealthyEnv.HOMEPAGE_CDN_URL}/partners-social-media-icons-twitter.svg`,
  url: `${WealthyEnv.TWITTER_SHARE_LINK}=http://invite.wlthy.in/?utm_source=share_twitter&text=Register%20as%20a%20Wealthy%20Partner%20and%20start%20your%20wealth%20management%20career.%20Sell%20financial%20products%20anytime%20and%20anywhere%20and%20earn%20regular%20income.%0A%0AJoin%20The%20Wealth%20Platform%20for%20Wealth%20Partners%3A%20`
};
const linkedInIcon = {
  altImage: "linkedIn-icon",
  image: `${WealthyEnv.HOMEPAGE_CDN_URL}/partners-social-media-icons-linkedIn.svg`,
  url: `${WealthyEnv.LINKEDIN_SHARE_LINK}=https://www.wealthy.in/partner?utm_source=share_linkedin&utm_medium=Referral&utm_campaign=Landing+Page+Referral&utm_term=Share+with+Friend`
};
const facebookIcon = {
  altImage: "facebook-icon",
  image: `${WealthyEnv.HOMEPAGE_CDN_URL}/partners-social-media-icons-facebook.svg`,
  url: `${WealthyEnv.FACEBOOK_SHARE_LINK}=http%3A%2F%2Finvite.wlthy.in%2F%3Futm_source%3Dshare_facebook&quote=Register%20as%20a%20Wealthy%20Partner%20and%20start%20your%20wealth%20management%20career.%20Sell%20financial%20products%20anytime%20and%20anywhere%20and%20earn%20regular%20income.%0A%0AJoin%20The%20Wealth%20Platform%20for%20Wealth%20Partners%3A%20http%3A%2F%2Finvite.wlthy.in%2F%3Futm_source%3Dshare_facebook`
};

const socialMediaIcons = [facebookIcon, whatsappIcon, linkedInIcon, twitterIcon];

const ThankYou = ({
  step,
  showLink,
  image,
  primaryText,
  secondaryText,
  showAppStoreButton,
  userData
}) => {
  useEffect(() => {
    if (step === 4) {
      const apiKey = window.sessionStorage.getItem("apiKey");
      const isExistingLead = window.sessionStorage.getItem("isExistingLead");
      if (isExistingLead === "true" && apiKey?.length > 0) {
        window.location.href = `${WealthyEnv.BUILDWEALTH_API_URL}/dashboards/api/v0/mobile-redirect/?token=${apiKey}&url=${WealthyEnv.BUILDWEALTH_APP_URL}/profile&app_version=partner-lp`;
        sessionStorage.clear();
      }
    }
  }, []);

  const copyShareLink = () => {
    const clipboard = document.querySelector(".copy-url");
    if (navigator.clipboard) {
      navigator.clipboard.writeText(clipboard.innerText)
        .then(text => {
          triggerToast("Copied text to clipboard", "success");
        })
        .catch(err => {
          triggerToast("Something went wrong");
        });
    } else {
      triggerToast("This feature not supported in your browser");
    }
  };

  return (
    <Wrapper>
      <Image>
        <img  alt="thankyou-image" className="thankyou-image" src={image} />
      </Image>
      <Title>{primaryText}</Title>
      <Subheader>{secondaryText}
      </Subheader>
      {
        showLink && (
          <CopyBoxContainer>
            <UrlContainer className="copy-url">
            http://invite.wlthy.in/?utm_source=share_form
            </UrlContainer>
            <CopyButton
              className="copy-button"
              onClick={copyShareLink}
            >
              <img alt="copy-button" className="copy-button-icon" src={copyIcon} />
            </CopyButton>

          </CopyBoxContainer>
        )
      }

      {
        showAppStoreButton && (
          <AppStoreLogoDisplay>
            <div id="download-app-android" onClick={() => window.open(`${WealthyEnv.PARTNER_APP_DOWNLOAD_LINK}`, "__blank")}>
              <img
                alt="Get it on Google Play"
                className="play-store-button"
                src="https://i.wlycdn.com/wealthy-home-page/play-store.svg"
              />
            </div>
          </AppStoreLogoDisplay>
        )
      }

      <TellAboutUsContainer marginTop={showLink}>
        <TellAboutUsTitle>Tell your friends about this</TellAboutUsTitle>
        <IconsContainer>
          {
            socialMediaIcons.map((icon) => {
              return (
                <Icon
                  href={icon.url}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <img alt={icon.altImage} className="each-icon" src={icon.image} />
                </Icon>
              );
            }
            )
          }
        </IconsContainer>
      </TellAboutUsContainer>
    </Wrapper>
  );
};

const AppStoreLogoDisplay = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  cursor: pointer;

  .play-store-button {
    width: 100%;
  }
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: 2rem;
  @media (max-width: 768px) {
    margin-top: 5rem;
  }
`;

const CopyButton = styled.div`
  border-left: 1px solid #c0c1d2;
  padding-left: 10px;
  cursor: pointer;

  .copy-button-icon {
    width: 2rem;
  }
`;

const UrlContainer = styled.div`
  font-size: 1.8rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 0 1rem;
`;

const CopyBoxContainer = styled.div`
  background: #e5e9ff;
  border-radius: 25px;
  align-items: center;
  color: #63668a;
  padding: 1rem 2rem;
  width: 70%;
  margin: 1rem auto 0;
  display: flex;
  justify-content: space-between;

  @media (max-width: 768px) {
    width: 100%;
    margin-top: 2rem;
  }
`;

const TellAboutUsContainer = styled.div`
  margin-top: ${props => props.marginTop ? "6.2rem" : "0"};
  width: 80rem;
  padding: 5rem;
  background-image: linear-gradient(to bottom, #efefef 0%, #f5f5f5 100%);

  @media (max-width: 768px) {
    width: 100%;
    margin-left: 0;
  }
`;

const Title = styled.h3`
  margin: 2rem 0 0.8rem 0;
  font-family: MavenPro;
  font-size: 2.4rem;
  font-weight: 600;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: center;
  color: #1c1c1c;

  @media(max-width: 768px) {
    font-size: 2.4rem;
  }
`;

const TellAboutUsTitle = styled(Title)`
  margin: 0;
  font-size: 2.4rem;

  @media(max-width: 768px) {
    font-size: 2.4rem;
  }

`;

const Subheader = styled(Title)`
  font-size: 1.8rem;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.43;
  letter-spacing: normal;
  text-align: center;
  color: #6c6c6c;
  margin-top: 2rem;
  .whatsapp-text {
    font-weight: 600;
    color: #1ba711;
  }

  @media(max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const Image = styled.div`
  .thankyou-image {
    object-fit: contain;
  }
`;

const IconsContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const Icon = styled(SafeLink)`
  width: 3.6rem;
  height: 3.6rem;
  margin: 2rem 1rem;
  :hover {
    cursor: pointer;
  }
  .each-icon {
    object-fit: contain;
  }
`;

export default ThankYou;
